import type { ProductListItem } from '@travel-planner/shared/schemas';
import { useDraggable } from '@dnd-kit/core';
import { resolveLocalizedText } from '@itineraries/entities/itinerary/lib/format';
import { useHoveredMarkerStore } from '@map-ui';
// Leaf imports, not the entity barrel: this card is mounted by the cms-public
// storefront, which cannot resolve the barrel's XT-only UI.
import { useAdultTravellerTypeIds } from '@products/entities/product/api/use-adult-traveller-type-ids';
import { formatPrice } from '@products/entities/product/lib/price';
import { extractDisplayPrice } from '@travel-planner/shared/pricing';
import { resolveThumbnailUrl } from '@travel-planner/shared/utils';
import { ImageOff, MapPin } from 'lucide-react';
import { getCategoryIcon } from '@/shared/lib/category-icon';
import { pickCss } from '@/shared/lib/css-utils';
import { AvgRoomNightPrice } from '@/shared/ui/avg-room-night-price';
import { Text } from '@/shared/ui/text';
import css from './source-product-card.module.scss';

// Phase 21b — drag id format. `<ItineraryDays>`'s onDragEnd handler
// splits on this prefix to route source-product drops into the
// `addProductToDay` mutation.
export const sourceProductDragId = (productId: string) =>
  `source-product:${productId}`;

const styles = pickCss(css, css.root);

type SourceProductCardProps = {
  product: ProductListItem;
  // Fired on a plain click (no drag). The planner's dnd-kit sensor has
  // a 6px activation distance, so a click that doesn't move never
  // starts a drag — the two gestures don't collide.
  onOpenDetails?: () => void;
  // The host itinerary's language. Legacy parity (`productsMaper` ->
  // `getValueByLanguage(product.title, itinerary.languageCode)`): the
  // library card shows the product title in the proposal's language,
  // falling back to the first available entry. `null`/unset -> 'en'.
  languageCode?: string | null;
  // Read-only historical-version view: dragging is the only way this card
  // writes into the itinerary, so the drag is disabled. Clicking for details
  // stays — that's a read.
  isReadOnly?: boolean;
  // Show the hotel's average historical room/night SELL price. Off by default
  // so a host opts in explicitly; both the console planner and the agent
  // portal do (it is a client-facing figure, not margin data).
  showAvgRoomNight?: boolean;
};

const readLocation = (product: ProductListItem): string | null => {
  if (product.city && product.country)
    return `${product.city}, ${product.country}`;
  return product.city ?? product.country ?? null;
};

// Pull the product's own time-of-day out of `legs[0].details.startTime`
// (`{hours, minutes}`) so the planner uses it as the new item's
// `startTime` HH:MM. Falls back to `null` when the product has no
// scheduled legs, in which case `useAddProductToDay`'s default
// (09:00) kicks in.
const readLegStartTime = (legs: unknown): string | null => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const first = legs[0] as { details?: unknown };
  const details = (first?.details ?? null) as { startTime?: unknown } | null;
  const start = (details?.startTime ?? null) as {
    hours?: unknown;
    minutes?: unknown;
  } | null;
  if (!start) return null;
  const h = typeof start.hours === 'number' ? start.hours : null;
  const m = typeof start.minutes === 'number' ? start.minutes : null;
  if (h == null || m == null || !Number.isInteger(h) || !Number.isInteger(m)) {
    return null;
  }
  if (h < 0 || h >= 24 || m < 0 || m >= 60) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// dnd-kit draggable card. The picker is portaled into the page's
// `.products` column from inside `<ItineraryDays>`'s `<DndContext>`,
// so `useDraggable` here registers against the same context as the
// day buckets' `useDroppable`. The drop is resolved by
// `<ItineraryDays>`'s `onDragEnd` (Phase 21b).
export type SourceProductDragData = {
  kind: 'source-product';
  productId: string;
  title: string;
  thumbnail: string | null;
  contentCategory: string | null;
  // HH:MM extracted from `product.legs[0].details.startTime` so the
  // planner's drop handler can preserve the product's own scheduled
  // time. `null` for products without legs — drop handler falls back
  // to its 09:00 default in that case.
  legStartTime: string | null;
};

export const SourceProductCard = ({
  product,
  onOpenDetails,
  languageCode,
  isReadOnly = false,
  showAvgRoomNight = false,
}: SourceProductCardProps) => {
  const setHovered = useHoveredMarkerStore((s) => s.setHovered);
  const title =
    resolveLocalizedText(product.title, languageCode ?? 'en') || '—';
  const location = readLocation(product);
  const CategoryIcon = getCategoryIcon(product.contentCategory);
  const adultTravellerIds = useAdultTravellerTypeIds();
  const displayPrice = extractDisplayPrice(product.price, adultTravellerIds);
  const priceText = formatPrice(displayPrice, product.currency);
  const thumbnailUrl = resolveThumbnailUrl(product.thumbnail);

  // Surface title + thumbnail + category through `data` so the
  // planner's `<DragOverlay>` can render a preview card without
  // re-querying the products list. `legStartTime` lets the drop
  // handler use the product's own scheduled time-of-day instead of
  // the 09:00 fallback.
  const dragData: SourceProductDragData = {
    kind: 'source-product',
    productId: product.id,
    title,
    thumbnail: thumbnailUrl,
    contentCategory: product.contentCategory,
    legStartTime: readLegStartTime(product.legs),
  };
  const draggable = useDraggable({
    id: sourceProductDragId(product.id),
    data: dragData,
    disabled: isReadOnly,
  });

  return (
    <button
      aria-label={
        isReadOnly
          ? `${title} — click for details`
          : `${title} — drag onto a day or click for details`
      }
      className={styles({ isDragging: draggable.isDragging })}
      data-product-id={product.id}
      onClick={() => onOpenDetails?.()}
      onMouseEnter={() => setHovered(product.id)}
      onMouseLeave={() => setHovered(null)}
      ref={draggable.setNodeRef}
      type="button"
      {...draggable.listeners}
      {...draggable.attributes}
    >
      <span className={css.thumb}>
        {thumbnailUrl ? (
          <img
            alt=""
            className={css.thumbImg}
            loading="lazy"
            src={thumbnailUrl}
          />
        ) : (
          <ImageOff
            aria-hidden="true"
            className={css.thumbPlaceholder}
            size={20}
          />
        )}
      </span>
      <span className={css.body}>
        <span className={css.titleRow}>
          <CategoryIcon
            aria-label={product.contentCategory ?? 'Uncategorized'}
            className={css.categoryIcon}
            size={14}
          />
          <Text as="span" className={css.title} size="sm" weight="medium">
            {title}
          </Text>
        </span>
        <span className={css.metaRow}>
          {location && (
            <span className={css.metaItem}>
              <MapPin aria-hidden="true" size={11} />
              {location}
            </span>
          )}
          <span className={css.metaItem}>{priceText}</span>
          {showAvgRoomNight && (
            <span className={css.metaItem}>
              <AvgRoomNightPrice avgRoomNight={product.avgRoomNight} />
            </span>
          )}
        </span>
      </span>
    </button>
  );
};
